"use client";

import React, { useEffect, useRef, useState } from "react";

export const IssueTag = ({ text }: { text: string }) => {
    const tagRef = useRef<HTMLDivElement>(null);
    const [showDot, setShowDot] = useState(true);

    useEffect(() => {
        const element = tagRef.current;
        if (!element) return;

        const checkIfLastInRow = () => {
            if (!element) return;

            const parent = element.parentElement;
            if (!parent) return;

            // Get all direct children of the parent
            const children = Array.from(parent.children);
            const index = children.indexOf(element);
            if (index === -1) return;

            // Check if this is the last element
            if (index === children.length - 1) {
                setShowDot(false);
                return;
            }

            // Get the next element
            const nextElement = children[index + 1];
            if (!nextElement) return;

            // Compare the top positions to see if they're in the same row
            const currentTop = element.getBoundingClientRect().top;
            const nextTop = nextElement.getBoundingClientRect().top;

            setShowDot(Math.abs(currentTop - nextTop) < 1); // If they're on the same row
        };

        const resizeObserver = new ResizeObserver(() => {
            checkIfLastInRow();
        });

        const debouncedCheck = debounce(checkIfLastInRow, 100);
        window.addEventListener("resize", debouncedCheck);

        // Start observing
        resizeObserver.observe(element);

        // Initial check
        checkIfLastInRow();

        // Cleanup
        return () => {
            window.removeEventListener("resize", debouncedCheck);
            if (element) {
                resizeObserver.unobserve(element);
            }
        };
    }, []);

    return (
        <div className="flex items-center" ref={tagRef}>
            <span className="px-5 py-2.5 border border-black rounded-full text-lg text-center md:text-left">
                {text}
            </span>
            <span
                className={`w-[10px] h-[10px] rounded-full bg-primary-100 ml-2 transition-opacity duration-200 hidden md:inline ${
                    showDot ? "opacity-100" : "opacity-0"
                }`}
            />
        </div>
    );
};

// Debounce helper to prevent too many resize checks
const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};
